/**
 * Access rules for the guided agentic wizard.
 * Distinguishes: guest · Free · Pro · Enterprise (+ DEV demo override).
 */

import type { UserProfile } from '../types';
import type {
  AgenticAccessState,
  AgenticAccessTier,
  AgenticStepAccess,
  AgenticStepId,
} from '../types/agenticEngagement';
import { AGENTIC_STEP_ORDER } from '../types/agenticEngagement';
import {
  FEATURES,
  PLAN_CONFIG,
  PLANS,
  PREMIUM_SURFACES,
  type UserPlan,
} from '../config/permissions';
import { normalizeUserPlan } from './userPlan';

/**
 * Wizard steps that are also PREMIUM_SURFACES.agentic kinds (derived — not a parallel list).
 * Filter from AGENTIC_STEP_ORDER → AgenticStepId (not the reverse): premium kinds like
 * `finops` / `techMapping` are not wizard steps and must not enter this Set.
 */
const PREMIUM_WIZARD_STEPS: ReadonlySet<AgenticStepId> = new Set(
  AGENTIC_STEP_ORDER.filter((step) =>
    (PREMIUM_SURFACES.agentic as readonly string[]).includes(step),
  ),
);

/** VIEW_PREMIUM_SECTIONS from PLAN_CONFIG — same feature flag as API. */
function planCanViewPremiumArtifacts(plan: string | undefined): boolean {
  const normalized = normalizeUserPlan(plan || PLANS.FREE) as UserPlan;
  const config = PLAN_CONFIG[normalized] || PLAN_CONFIG[PLANS.FREE];
  return Boolean(config.features[FEATURES.VIEW_PREMIUM_SECTIONS]);
}

export const AGENTIC_DEMO_STORAGE_KEY = 'agenticDemoAccess';
export const AGENTIC_DEMO_CHANGE_EVENT = 'agentic-demo-access';

export type AgenticDemoOverride = 'guest' | 'free' | 'pro' | 'enterprise' | null;

export function readDemoOverride(): AgenticDemoOverride {
  if (typeof window === 'undefined') return null;
  // Only allow in CRA/Vite development builds
  if (process.env.NODE_ENV === 'production') return null;
  const raw = localStorage.getItem(AGENTIC_DEMO_STORAGE_KEY);
  if (raw === 'guest' || raw === 'free' || raw === 'pro' || raw === 'enterprise') return raw;
  return null;
}

export function writeDemoOverride(value: AgenticDemoOverride) {
  if (process.env.NODE_ENV === 'production') return;
  if (!value) localStorage.removeItem(AGENTIC_DEMO_STORAGE_KEY);
  else localStorage.setItem(AGENTIC_DEMO_STORAGE_KEY, value);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AGENTIC_DEMO_CHANGE_EVENT));
  }
}

function tierFromPlan(plan: string | undefined, isAuthenticated: boolean): AgenticAccessTier {
  if (!isAuthenticated) return 'guest';
  const normalized = normalizeUserPlan(plan);
  if (normalized === 'Enterprise') return 'enterprise';
  if (normalized === 'Pro') return 'pro';
  return 'free';
}

/** Prefer live profile quota; never invent a high fake number that never decrements. */
function resolveQueriesLeft(
  userProfile: UserProfile | null | undefined,
  fallbackWhenUnknown: number
): number {
  if (typeof userProfile?.queriesLeft === 'number') {
    return Math.max(0, userProfile.queriesLeft);
  }
  return Math.max(0, fallbackWhenUnknown);
}

/**
 * Resolve effective access for UI gating.
 * DEV DemoAccessSwitcher can preview tiers in the shell only — API/PDF remain authoritative
 * (see AUTH.md). Prefer Real auth for billing-accurate checks.
 */
export function resolveAgenticAccess(
  isAuthenticated: boolean,
  userProfile: UserProfile | null | undefined
): AgenticAccessState {
  const demo = readDemoOverride();
  const isDemoOverride = demo !== null;

  if (demo === 'guest') {
    return {
      tier: 'guest',
      isAuthenticated: false,
      plan: 'Guest',
      queriesLeft: 0,
      canUseAi: false,
      canViewPremiumArtifacts: false,
      isDemoOverride,
    };
  }
  if (demo === 'free') {
    const queriesLeft = resolveQueriesLeft(userProfile, 0);
    return {
      tier: 'free',
      isAuthenticated: Boolean(isAuthenticated),
      plan: 'Free',
      queriesLeft,
      // Demo UI tier ≠ free AI: still need real auth + remaining quota
      canUseAi: Boolean(isAuthenticated) && queriesLeft > 0,
      canViewPremiumArtifacts: false,
      isDemoOverride,
    };
  }
  if (demo === 'pro') {
    const queriesLeft = resolveQueriesLeft(userProfile, 0);
    return {
      tier: 'pro',
      isAuthenticated: Boolean(isAuthenticated),
      plan: 'Pro',
      queriesLeft,
      canUseAi: Boolean(isAuthenticated) && queriesLeft > 0,
      canViewPremiumArtifacts: planCanViewPremiumArtifacts(PLANS.PRO),
      isDemoOverride,
    };
  }
  if (demo === 'enterprise') {
    const queriesLeft = resolveQueriesLeft(userProfile, 0);
    return {
      tier: 'enterprise',
      isAuthenticated: Boolean(isAuthenticated),
      plan: 'Enterprise',
      queriesLeft,
      canUseAi: Boolean(isAuthenticated) && queriesLeft > 0,
      canViewPremiumArtifacts: planCanViewPremiumArtifacts(PLANS.ENTERPRISE),
      isDemoOverride,
    };
  }

  const tier = tierFromPlan(userProfile?.plan, isAuthenticated);
  const queriesLeft = resolveQueriesLeft(userProfile, 0);
  const canUseAi = isAuthenticated && queriesLeft > 0;
  const canonicalPlan = isAuthenticated
    ? normalizeUserPlan(userProfile?.plan)
    : 'Guest';
  const canViewPremiumArtifacts = isAuthenticated && planCanViewPremiumArtifacts(canonicalPlan);

  return {
    tier,
    isAuthenticated,
    plan: canonicalPlan,
    queriesLeft,
    canUseAi,
    canViewPremiumArtifacts,
    isDemoOverride: false,
  };
}

/**
 * What access a step requires / how UI should gate it.
 */
export function getStepAccess(
  stepId: AgenticStepId,
  access: AgenticAccessState
): AgenticStepAccess {
  if (stepId === 'discovery' || stepId === 'requirements' || stepId === 'summary') {
    return 'open';
  }

  if (stepId === 'architecture') {
    // Guest + Free without AI: local catalog sketch (TTFV). Account required at Recommendation.
    if (!access.canUseAi) return 'open';
    return 'full';
  }

  // Premium pack wizard steps — derived from PREMIUM_SURFACES ∩ AGENTIC_STEP_ORDER
  if (PREMIUM_WIZARD_STEPS.has(stepId)) {
    if (access.tier === 'guest') return 'login_required';
    if (!access.canViewPremiumArtifacts) return 'premium';
    return 'full';
  }

  return 'open';
}
